import type { AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import axios, { isAxiosError } from 'axios'

import {
  addRequestMetadata,
  calculateRequestDuration,
  captchaBypassHeaders,
  createMalformedResponseError,
  extractTelemetryId,
  isMalformedResponse,
  logFailedRequest,
  logRequestStart,
  logSuccessfulResponse,
  materializeFormDataFiles,
} from './api-client.utils'

export const verificationClient = axios.create({
  baseURL: import.meta.env.VITE_VERIFICATION_API_URL,
  headers: {
    ['Content-Type']: 'application/json',
  },
})

verificationClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const withMetadata = addRequestMetadata(config)
    withMetadata.headers.set(captchaBypassHeaders())

    const updatedConfig = await materializeFormDataFiles(withMetadata)
    logRequestStart(updatedConfig)

    return updatedConfig
  },
)

verificationClient.interceptors.response.use(
  (response: AxiosResponse) => {
    const duration = calculateRequestDuration(response.config)
    const telemetryOperationId = extractTelemetryId(response)

    if (isMalformedResponse(response)) {
      const error = createMalformedResponseError(response)
      logFailedRequest(error, duration, telemetryOperationId)
      return Promise.reject(error)
    }

    logSuccessfulResponse(response, duration, telemetryOperationId)

    return response
  },
  (error: unknown) => {
    if (!isAxiosError(error)) {
      return Promise.reject(error)
    }

    const duration = calculateRequestDuration(
      error.config as InternalAxiosRequestConfig,
    )
    logFailedRequest(error, duration, extractTelemetryId(error.response))

    return Promise.reject(error)
  },
)
